import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Scissors, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatformLogin = location.pathname.startsWith("/saas");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(user.role === "super_admin" ? "/saas" : "/admin", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const loggedIn = await login({ email, password });
      navigate(loggedIn.role === "super_admin" ? "/saas" : "/admin", { replace: true });
    } catch {
      toast.error(isPlatformLogin ? "Usuário ou senha inválidos." : "E-mail ou senha inválidos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1536520002442-39764a41e987?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0E]/80 via-[#0A0B0E]/90 to-[#0A0B0E]" />

      <div className="relative z-10 w-full max-w-md fade-in">
        <Link
          to="/"
          data-testid="back-to-public-link"
          className="inline-flex items-center gap-2 text-stone-400 hover:text-[#D4AF37] mb-8 text-sm font-sans2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para agendamento
        </Link>

        <div className="card-surface p-8 sm:p-10 text-center" style={{ background: "#12141A" }}>
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-6">
            <Scissors className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl gold-text mb-1">AgendaCut</h1>
          <p className="font-sans2 text-stone-400 text-sm mb-8 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-4 h-4" /> {isPlatformLogin ? "Administração da plataforma" : "Área administrativa restrita"}
          </p>

          <form onSubmit={handleLogin} className="space-y-4 text-left">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm text-stone-300 font-sans2">{isPlatformLogin ? "Usuário/Login" : "E-mail"}</label>
              <input
                id="admin-email"
                data-testid="admin-login-email-input"
                type={isPlatformLogin ? "text" : "email"}
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                className="w-full rounded-lg border border-[#282C37] bg-[#0A0B0E] px-4 py-3 text-amber-50 placeholder-stone-600 focus:border-[#D4AF37] focus:outline-none font-sans2"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm text-stone-300 font-sans2">Senha</label>
              <input
                id="admin-password"
                data-testid="admin-login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="w-full rounded-lg border border-[#282C37] bg-[#0A0B0E] px-4 py-3 text-amber-50 placeholder-stone-600 focus:border-[#D4AF37] focus:outline-none font-sans2"
              />
            </div>
            <button
              data-testid="admin-login-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#D4AF37] text-[#0A0B0E] font-sans2 font-semibold py-3 rounded-lg hover:bg-[#E5C158] transition-colors disabled:opacity-60"
            >
              {submitting ? "Entrando…" : "Entrar"}
            </button>
            {isPlatformLogin && <Link to="/saas/esqueci-senha" className="block text-center text-sm text-[#D4AF37] hover:text-[#E5C158]">Esqueci minha senha</Link>}
          </form>

          <p className="text-xs text-stone-500 mt-6 font-sans2">
            Acesso permitido apenas a administradores cadastrados.
          </p>
        </div>
      </div>
    </div>
  );
}
